import SearchIcon from "@mui/icons-material/Search";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import { Avatar, Box, Checkbox, Chip, InputAdornment, List, ListItemButton, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Contact, useInternalContacts } from "../../internal-contacts";
import { useGetContacts } from "../../virtual-receptionist/contacts/hooks/useContacts";
import { IContact } from "../../virtual-receptionist/types/contacts.types";
import { formatUSPhone } from "../../../utils";
import { MessageGroupMemberInput, MessageGroupMemberType } from "../messageGroups.types";
import { getAvatarColor } from "../utils/messageGroupDisplay.utils";

export type MemberPickerMode = "internal" | "sms";

export interface PickableMember extends MessageGroupMemberInput {
  name: string;
  avatar: string | null;
  subtitle: string | null;
  isExternal?: boolean;
}

const CONTACT_TYPE_TO_MEMBER_TYPE: Record<string, MessageGroupMemberType> = {
  workforce: "workforce",
  organizationCoworker: "user",
};

const toPickableMember = (contact: Contact): PickableMember | null => {
  const type = CONTACT_TYPE_TO_MEMBER_TYPE[contact.type];
  if (!type || !contact.uid) return null;

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || contact.name || contact.email || "Unknown";

  return {
    type,
    uid: contact.uid,
    name,
    avatar: contact.avatar ?? null,
    subtitle: type === "workforce" ? "Workforce" : "Organization",
  };
};

export const toE164 = (value?: string | null): string | null => {
  let digits = String(value ?? "").replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  if (/^[01]/.test(digits.slice(0, 3)) || /^[01]/.test(digits.slice(3, 6))) return null;

  return `+1${digits}`;
};

export const buildContactMember = (
  phoneNumber: string,
  displayName?: string | null,
  isFlownectNumber = false,
): PickableMember => {
  const resolvedName = displayName?.trim() || "";
  const name = resolvedName || formatUSPhone(phoneNumber) || phoneNumber;

  return {
    type: isFlownectNumber ? "companyNumber" : "contact",
    uid: phoneNumber,
    phoneNumber,
    ...(resolvedName ? { displayName: resolvedName } : {}),
    name,
    avatar: null,
    subtitle: formatUSPhone(phoneNumber) || phoneNumber,
    isExternal: true,
  };
};

const toPickableSmsContact = (contact: IContact): PickableMember | null => {
  const rawPhone = contact.phoneNumbers?.find((item) => item.isPrimary)?.number ?? contact.phoneNumbers?.[0]?.number ?? contact.phoneNumber;
  const phoneNumber = toE164(rawPhone);

  if (!phoneNumber) return null;

  return buildContactMember(phoneNumber, [contact.name, contact.lastName].filter(Boolean).join(" ").trim());
};

export const memberKeyOf = (member: { type: string; uid: string }) => `${member.type}:${member.uid}`;

interface MemberPickerProps {
  selected: PickableMember[];
  onChange: (members: PickableMember[]) => void;
  mode?: MemberPickerMode;
  companyId?: number | string | null;
  excludeKeys?: string[];
  emptyLabel?: string;
}

export const MemberPicker = ({
  selected,
  onChange,
  mode = "internal",
  companyId,
  excludeKeys = [],
  emptyLabel = "No contacts available",
}: MemberPickerProps) => {
  const isSmsMode = mode === "sms";
  const { contacts, isLoading } = useInternalContacts();
  const [search, setSearch] = useState("");

  const numericCompanyId = Number(companyId ?? 0);
  const { data: smsContactsRes, isLoading: isLoadingSmsContacts } = useGetContacts(numericCompanyId, 1, 100, undefined, {
    enabled: isSmsMode && Boolean(numericCompanyId),
    type: "",
  });

  const smsContacts = useMemo<IContact[]>(() => {
    const payload = smsContactsRes as any;
    if (!payload) return [];
    return Array.isArray(payload) ? payload : (payload.data ?? []);
  }, [smsContactsRes]);

  const excluded = useMemo(() => new Set(excludeKeys), [excludeKeys]);
  const selectedKeys = useMemo(() => new Set(selected.map(memberKeyOf)), [selected]);

  const options = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    const source = isSmsMode ? smsContacts.map(toPickableSmsContact) : contacts.map(toPickableMember);

    return source
      .filter((member): member is PickableMember => member !== null)
      .filter((member) => !excluded.has(memberKeyOf(member)))
      .filter((member) => (normalized ? `${member.name} ${member.subtitle ?? ""}`.toLowerCase().includes(normalized) : true))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts, smsContacts, isSmsMode, search, excluded]);

  const toggle = (member: PickableMember) => {
    const key = memberKeyOf(member);
    if (selectedKeys.has(key)) {
      onChange(selected.filter((item) => memberKeyOf(item) !== key));
      return;
    }
    onChange([...selected, member]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minHeight: 0 }}>
      <TextField
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={isSmsMode ? "Search contacts..." : "Search people..."}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {selected.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selected.map((member) => (
            <Chip
              key={memberKeyOf(member)}
              label={member.name}
              size="small"
              onDelete={() => toggle(member)}
              avatar={
                <Avatar src={member.avatar || undefined} sx={{ bgcolor: member.avatar ? undefined : getAvatarColor(member.name) }}>
                  {member.name[0]?.toUpperCase()}
                </Avatar>
              }
            />
          ))}
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 220, maxHeight: 300, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        {isLoading || isLoadingSmsContacts ? (
          <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>Loading contacts...</Typography>
        ) : options.length === 0 ? (
          <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>{emptyLabel}</Typography>
        ) : (
          <List dense disablePadding>
            {options.map((member) => {
              const key = memberKeyOf(member);
              const isSelected = selectedKeys.has(key);

              return (
                <ListItemButton key={key} onClick={() => toggle(member)} sx={{ gap: 1.25, py: 0.75 }}>
                  <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple size="small" sx={{ p: 0.5 }} />
                  <Avatar
                    src={member.avatar || undefined}
                    alt={member.name}
                    sx={{ width: 30, height: 30, fontSize: "0.75rem", bgcolor: member.avatar ? undefined : getAvatarColor(member.name) }}
                  >
                    {member.name[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                      {member.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {member.isExternal && <SmsRoundedIcon sx={{ fontSize: 12, color: "warning.main" }} />}
                      <Typography noWrap sx={{ fontSize: 11, color: "text.secondary" }}>
                        {member.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};
